import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { apiUrl } from '../utils/api'
import { ArrowLeft, CheckCircle2, Clock, ChevronLeft, ChevronRight, BookOpen, HelpCircle } from 'lucide-react'
import '../styles/course.css'

interface Card {
  id: string
  html: string
}

interface ModuleContent {
  id: string
  headline: string
  subheadline: string | null
  summary: string | null
  heroImageUrl: string | null
  readTime: number | null
  cards: Card[]
}

interface NavModule {
  id: string
  headline: string
  done: boolean
  hasQuiz: boolean
}

export default function ModuleView() {
  const { courseId, moduleId } = useParams()
  const { token } = useAuth()
  const navigate = useNavigate()

  const [module, setModule] = useState<ModuleContent | null>(null)
  const [hasQuiz, setHasQuiz] = useState(false)
  const [done, setDone] = useState(false)
  const [navModules, setNavModules] = useState<NavModule[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const load = async () => {
      setIsLoading(true)
      setError('')
      try {
        const [moduleRes, courseRes] = await Promise.all([
          fetch(apiUrl(`/api/courses/${courseId}/modules/${moduleId}`), {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(apiUrl(`/api/courses/${courseId}`), {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ])
        const moduleData = await moduleRes.json()
        if (!moduleRes.ok) throw new Error(moduleData.error || 'Failed to load module')

        setModule(moduleData.module)
        setHasQuiz(!!moduleData.hasQuiz)
        setDone(!!moduleData.done)

        const courseData = await courseRes.json()
        if (courseRes.ok && Array.isArray(courseData.modules)) {
          setNavModules(
            courseData.modules.map((m: any) => ({
              id: m.id,
              headline: m.headline,
              done: !!m.done,
              hasQuiz: !!m.hasQuiz,
            }))
          )
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load module')
      } finally {
        setIsLoading(false)
      }
    }
    load()
    window.scrollTo(0, 0)
  }, [courseId, moduleId, token])

  const currentIndex = navModules.findIndex((m) => m.id === moduleId)
  const nextModule = currentIndex >= 0 ? navModules[currentIndex + 1] : undefined
  const prevModule = currentIndex > 0 ? navModules[currentIndex - 1] : undefined

  const markRead = async (): Promise<boolean> => {
    setSaving(true)
    try {
      const res = await fetch(
        apiUrl(`/api/courses/${courseId}/modules/${moduleId}/complete`),
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
      )
      if (!res.ok) throw new Error('Failed to save progress')
      setDone(true)
      return true
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save progress')
      return false
    } finally {
      setSaving(false)
    }
  }

  const handleMarkAndNext = async () => {
    if (!done) {
      const ok = await markRead()
      if (!ok) return
    }
    if (nextModule) navigate(`/course/${courseId}/module/${nextModule.id}`)
    else navigate(`/course/${courseId}`)
  }

  if (isLoading) return <div className="loading">Loading lesson…</div>

  if (error) {
    return (
      <div className="module-view-container">
        <div className="error-message" style={{ maxWidth: 760, margin: '40px auto', padding: '0 24px' }}>{error}</div>
        <div style={{ maxWidth: 760, margin: '0 auto', padding: '0 24px' }}>
          <button className="back-link-btn" onClick={() => navigate(`/course/${courseId}`)}>
            <ArrowLeft size={15} /> Back to Course
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="module-view-container">
      <div className="module-topbar">
        <button className="back-link-btn" onClick={() => navigate(`/course/${courseId}`)}>
          <ArrowLeft size={15} /> Back to Course
        </button>
        <div className="module-topbar-right">
          {done && (
            <span className="completed-pill">
              <CheckCircle2 size={13} /> Completed
            </span>
          )}
          {hasQuiz && (
            <button
              className="quiz-launch-btn"
              onClick={() => navigate(`/course/${courseId}/module/${moduleId}/quiz`)}
            >
              <HelpCircle size={14} /> Take Quiz
            </button>
          )}
        </div>
      </div>

      <article className="module-article">
        <header className="module-article-header">
          <h1>{module?.headline}</h1>
          {module?.subheadline && <p className="module-subheadline">{module.subheadline}</p>}
          {module?.readTime && (
            <span className="module-read-time-pill">
              <Clock size={13} /> {module.readTime} min read
            </span>
          )}
        </header>

        {module?.heroImageUrl && (
          <img className="module-hero-img" src={module.heroImageUrl} alt={module.headline} />
        )}

        <div className="module-content">
          {module?.cards.map((card) => (
            <div
              key={card.id}
              className="module-card"
              dangerouslySetInnerHTML={{ __html: card.html }}
            />
          ))}
        </div>
      </article>

      {error && <div className="error-message" style={{ maxWidth: 760, margin: '12px auto', padding: '0 24px' }}>{error}</div>}

      <footer className="module-footer">
        <button
          className="nav-btn"
          disabled={!prevModule}
          onClick={() => prevModule && navigate(`/course/${courseId}/module/${prevModule.id}`)}
        >
          <ChevronLeft size={16} /> Prev
        </button>

        <button className="complete-btn" onClick={handleMarkAndNext} disabled={saving}>
          {saving ? 'Saving…' : done
            ? nextModule ? <>Next <ChevronRight size={16} /></> : <><BookOpen size={15} /> Back to Course</>
            : nextModule ? <><CheckCircle2 size={15} /> Mark Read & Next</> : <><CheckCircle2 size={15} /> Mark Complete</>
          }
        </button>

        <button
          className="nav-btn"
          disabled={!nextModule}
          onClick={() => nextModule && navigate(`/course/${courseId}/module/${nextModule.id}`)}
        >
          Next <ChevronRight size={16} />
        </button>
      </footer>
    </div>
  )
}


interface Card {
  id: string
  html: string
}

interface ModuleContent {
  id: string
  headline: string
  subheadline: string | null
  summary: string | null
  heroImageUrl: string | null
  readTime: number | null
  cards: Card[]
}

interface QuizOption {
  id: string
  text: string
}

interface QuizQuestion {
  id: string
  text: string
  options: QuizOption[]
}

interface Quiz {
  questions: QuizQuestion[]
}

interface QuestionResult {
  questionId: string
  correctOptionId: string | null
  selectedOptionId: string | null
  correct: boolean
}

interface QuizResult {
  score: number
  total: number
  percentage: number
  results: QuestionResult[]
}

interface NavModule {
  id: string
  headline: string
  done: boolean
  locked: boolean
}
